import React from 'react';
import { CONTENT_MAX_WIDTH } from '../../styles/tokens';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useLanguage } from '../../context/LanguageContext';
import { BORDER, FOCUS_RING, ICON_QUIET, INK, MUTED, SHADOW_CARD, SURFACE } from '../../pages/Practice/map/mapTokens';
import { WHAT_YOU_GET, WHAT_YOU_GET_HEADING } from './homeContent';
import { AccessBadge, IconTile, SectionHeading, bandSx } from './homeUi';

/**
 * The section the homepage was missing entirely: a plain statement of what the
 * product contains. Everything listed here already existed — a vocabulary
 * library, a flashcard trainer, fourteen real exam videos, guided practice, mock
 * exams, tips — and none of it was mentioned on the page.
 */
const WhatYouGet = ({ tone }) => {
  const { language } = useLanguage();

  return (
    <Box component="section" sx={bandSx(tone)}>
      <SectionHeading title={WHAT_YOU_GET_HEADING.title} subtitle={WHAT_YOU_GET_HEADING.subtitle} />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, md: 3 },
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        {WHAT_YOU_GET.map((item) => (
          <Box
            key={item.key}
            component={RouterLink}
            to={typeof item.to === 'function' ? item.to(language) : item.to}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: { xs: 2.5, md: 3 },
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: '16px',
              boxShadow: SHADOW_CARD,
              textDecoration: 'none',
              transition: 'transform .22s cubic-bezier(.2,.8,.2,1), box-shadow .22s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 1px 2px rgba(16,24,40,.05), 0 22px 34px -16px rgba(19,62,135,.35)',
              },
              '&:hover .whatYouGet-arrow': { transform: 'translateX(3px)' },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': { transform: 'none' },
                '&:hover .whatYouGet-arrow': { transform: 'none' },
              },
              ...FOCUS_RING,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
              <IconTile name={item.icon} />
              <AccessBadge access={item.access} />
            </Box>

            <Typography variant="h6" component="h3" sx={{ color: 'primary.main', fontWeight: 800, lineHeight: 1.3 }}>
              {item.title}
            </Typography>

            <Typography sx={{ color: INK, fontSize: '0.9375rem', lineHeight: 1.65, flexGrow: 1 }}>
              {item.text}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: MUTED, fontWeight: 700 }}>
              <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'primary.main' }}>
                Open
              </Typography>
              <ArrowForward
                className="whatYouGet-arrow"
                aria-hidden
                sx={{ fontSize: 16, color: ICON_QUIET, transition: 'transform .2s ease' }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default WhatYouGet;
